/**
 * BookDataService - Unified service for managing book data (spine.json, word files)
 * Works with both local development and S3 production environments
 */

import { authenticatedFetch, authenticatedPut } from '../utils/api';

export interface BookDataConfig {
  baseUrl: string;
  isbn: string;
}

export interface SpineData {
  pages: PageData[];
  metadata?: any;
}

export interface PageData {
  'page-index': number;
  filename: string;
  original?: string;
  text_extracted?: string;
  words?: string;
  'words-enhanced'?: string;
  width: number;
  height: number;
  pageType?: string;
}

export interface WordData {
  // Enhanced JSON structure
  library?: string;
  timestamp?: string;
  image_filename?: string;
  image_dimensions?: { width: number; height: number };
  total_blocks?: number;
  total_words?: number;
  page?: {
    pageType: string;
    review: string;
    possibleIssue: string;
    'ai-confidence': number;
    voiceAttributes: string;
  };
  blocks?: Array<{
    blockId: number;
    blockType: string;
    readBlock: boolean;
    words: Array<{
      wordId: number;
      text: string;
      boundingBox: { x: number; y: number; width: number; height: number };
      confidence: number;
    }>;
    boundingBox: { x: number; y: number; width: number; height: number };
    blockReadOrder: number;
    modifiedWords?: string | null;
    mergedFromBlocks?: number[] | null;
    review: string;
    possibleIssue: string;
    'ai-confidence': number;
  }>;
  [key: string]: any;
}

export class BookDataService {
  private baseUrl: string;
  private isbn: string;

  constructor(config: BookDataConfig) {
    this.baseUrl = config.baseUrl;
    this.isbn = config.isbn;
  }

  /**
   * Load spine.json data
   */
  async loadSpineData(): Promise<SpineData> {
    const url = `${this.baseUrl}/api/books/${this.isbn}/spine`;
    
    const response = await authenticatedFetch(url);
    if (!response.ok) {
      throw new Error(`Failed to load spine data: ${response.status} ${response.statusText}`);
    }
    
    const data = await response.json();
    return data;
  }

  /**
   * Load word data for a specific page
   * Only uses enhanced JSON format - words -> words-enhanced fallback
   */
  async loadWordData(wordsFilename: string): Promise<WordData> {
    // Try primary filename first
    let result = await this.tryLoadWordFile(wordsFilename);
    if (result.success) return result.data;

    // If enhanced JSON doesn't exist, try words-enhanced fallback
    if (wordsFilename.includes('-enhanced.json')) {
      // Already trying enhanced, no fallback needed
      throw new Error(`Enhanced word data file not found: ${wordsFilename}`);
    }

    // If it's a words file, try words-enhanced fallback
    if (wordsFilename.includes('.json')) {
      const enhancedFilename = wordsFilename.replace('.json', '-enhanced.json');
      result = await this.tryLoadWordFile(enhancedFilename);
      if (result.success) return result.data;
    }

    throw new Error(`No enhanced word data file found for: ${wordsFilename}`);
  }

  /**
   * Try to load a specific word file
   */
  private async tryLoadWordFile(filename: string): Promise<{ success: boolean; data?: WordData; error?: string }> {
    const { FolderService } = await import('./FolderService');
    const folder = FolderService.getFolder();
    if (!folder) {
      return { success: false, error: 'Folder is required but not set' };
    }
    const url = FolderService.getPageJsonUrl(this.isbn, filename);
    
    try {
      const response = await authenticatedFetch(url);
      
      if (!response.ok) {
        return { success: false, error: `File not found: ${filename}` };
      }
      
      const data = await response.json();
      return { success: true, data };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      return { success: false, error: errorMessage };
    }
  }

  /**
   * Save spine data
   */
  async saveSpineData(spineData: SpineData): Promise<void> {
    const url = `${this.baseUrl}/api/books/${this.isbn}/spine`;
    
    const response = await authenticatedPut(url, spineData);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(`Failed to save spine data: ${response.status} ${response.statusText} - ${errorData.error || 'Unknown error'}`);
    }
  }

  /**
   * Save word data for a specific page
   */
  async saveWordData(wordsFilename: string, wordData: WordData, pageIndex?: number): Promise<void> {
    const { FolderService } = await import('./FolderService');
    const url = FolderService.getPageJsonUrl(this.isbn, wordsFilename);
    
    const response = await authenticatedPut(url, {
      pageIndex: pageIndex || 0, // Default to 0 if not provided
      data: wordData
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(`Failed to save word data: ${response.status} ${response.statusText} - ${errorData.error || 'Unknown error'}`);
    }
  }

  /**
   * Determine the best word file strategy for a page
   * Only uses words -> words-enhanced fallback
   */
  getWordDataStrategy(page: PageData): string | null {
    // Skip pages that shouldn't have word data
    const skipWordDataTypes = ['blank-page', 'no-text'];
    if (page.pageType && skipWordDataTypes.includes(page.pageType)) {
      return null;
    }
    
    // Use spine.json data - only use words field
    if (page.words) {
      return page.words;
    }
    
    return null;
  }

  /**
   * Get page data by page index
   */
  getPageByIndex(spineData: SpineData, pageIndex: number): PageData | null {
    return spineData.pages?.find(page => page['page-index'] === pageIndex) || null;
  }

  /**
   * Update page data in spine
   */
  updatePageInSpine(spineData: SpineData, updatedPage: PageData): SpineData {
    const updatedSpine = { ...spineData };
    const pageIndex = updatedPage['page-index'];
    
    updatedSpine.pages = updatedSpine.pages.map(page => 
      page['page-index'] === pageIndex ? updatedPage : page
    );
    
    return updatedSpine;
  }
}

/**
 * Factory function to create BookDataService instance
 */
export function createBookDataService(isbn: string, baseUrl: string = ''): BookDataService {
  return new BookDataService({ baseUrl, isbn });
}

export default BookDataService;
